import { useEffect, useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from '@sakura-grp/ui/drawer';
import { Button } from '@sakura-grp/ui/button';
import { Pencil } from 'lucide-react';
import { useUpdateActivityDate } from '@/hooks/useActivities';
import { ACTIVITY_DATE_INPUT_MAX, ACTIVITY_DATE_INPUT_MIN, parseActivityDateInput } from '@/lib/activity-date-input';

interface EditActivityDateSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activity: {
    id: string;
    activityDateIso: string;
    type: string;
    storeName?: string;
    customerName?: string;
  } | null;
}

function toLocalInputValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function EditActivityDateSheet({
  open,
  onOpenChange,
  activity,
}: EditActivityDateSheetProps) {
  const updateActivityDate = useUpdateActivityDate();
  const [value, setValue] = useState('');

  useEffect(() => {
    if (activity) {
      setValue(toLocalInputValue(activity.activityDateIso));
    }
  }, [activity]);

  if (!activity) return null;

  const parsedIso = parseActivityDateInput(value);
  const isInvalid = value !== '' && parsedIso === null;

  const handleSave = () => {
    if (!parsedIso) return;
    const iso = parsedIso;
    updateActivityDate.mutate(
      { id: activity.id, activityDate: iso },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      }
    );
  };

  const isUnchanged = value === toLocalInputValue(activity.activityDateIso);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="px-lg pb-safe-min">
        <DrawerHeader className="pb-md">
          <DrawerTitle className="flex items-center gap-2">
            <Pencil className="size-5 text-brand" />
            営業報告時刻を訂正
          </DrawerTitle>
          <DrawerDescription>
            打ち間違いなど、誤った報告時刻を訂正できます。変更履歴は記録されます。
          </DrawerDescription>
        </DrawerHeader>

        <div className="space-y-4">
          <div className="rounded-lg bg-gray-50 p-3 text-sm">
            {activity.storeName && (
              <p><span className="text-gray-500">店舗:</span> {activity.storeName}</p>
            )}
            <p><span className="text-gray-500">種別:</span> {activity.type}</p>
            {activity.customerName && (
              <p><span className="text-gray-500">顧客:</span> {activity.customerName}</p>
            )}
          </div>

          <label className="block">
            <span className="text-sm text-gray-600">新しい報告時刻</span>
            <input
              type="datetime-local"
              min={ACTIVITY_DATE_INPUT_MIN}
              max={ACTIVITY_DATE_INPUT_MAX}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              aria-invalid={isInvalid}
              aria-describedby={isInvalid ? 'activity-date-error' : undefined}
              className="mt-1 w-full rounded-md border px-3 py-2 text-md"
            />
          </label>
          {isInvalid && (
            <p id="activity-date-error" role="alert" className="text-sm text-destructive">
              日時が正しくありません（2000〜2099 年で入力してください）
            </p>
          )}
        </div>

        <DrawerFooter className="p-0 pt-md flex flex-col gap-md">
          <Button
            onClick={handleSave}
            disabled={updateActivityDate.isPending || !parsedIso || isUnchanged}
          >
            {updateActivityDate.isPending ? '保存中...' : '保存する'}
          </Button>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateActivityDate.isPending}
          >
            やめる
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
